//
// Copyright (c) 2011-2024 by Curalate, Inc.
//

import { FeatureNames } from '@bvengineering/curalate-features';
import {
  IStateParamsService,
  IStateService,
  IUrlRouterProvider,
} from 'angular-ui-router';
import stubTrue from 'lodash/fp/stubTrue';

import * as instagramHashtagApi from '@apis/instagram-hashtag/instagramHashtagApi';
import { IInstagramHashtagTrackedHashtagsResponse } from '@apis/instagram-hashtag/instagramHashtagApiModel';

import {
  IPartialState,
  NavGroup,
  StateFactory,
  componentNameToTemplate,
  hasFeature,
  lazyLoadModule,
  lazyLoadSubModule,
} from 'app/initialization/routing/stateHelpers';
import { PROMOTED_POSTS_ROUTES } from 'app/promoted-posts/promoted-posts-constants/promoted-posts-constants';
import { PROMOTED_POSTS_METRICS_ROUTES } from 'app/promoted-posts-metrics/promoted-posts-metrics-constants/promoted-posts-metrics-constants';

// Sets default values for state data
function InstagramStateFactory(state: IPartialState) {
  const accessFn = state.data?.hasAccess || stubTrue;
  const data = {
    title: 'Instagram Account',
    navGroup: NavGroup.PUBLISHING,
    ...state.data,
    hasAccess: () => hasFeature(FeatureNames.SocialDashboards) && accessFn(),
  };
  return StateFactory({
    ...state,
    data,
  });
}
function lazyLoadInstagramBundle(importFn: () => Promise<unknown>) {
  return lazyLoadSubModule('curalate.instagram-account.app', importFn);
}
export function routingRules(urlRouterProvider: IUrlRouterProvider) {
  // Existing route redirect rules
  urlRouterProvider.when('/instagram', '/instagram/summary');
  urlRouterProvider.when('/instagram/schedule', '/instagram/schedule/calendar');
  urlRouterProvider.when('/instagram/schedule/calendar', '/schedule/calendar');
  urlRouterProvider.when('/instagram/audience/summary', '/instagram/audience');
  urlRouterProvider.when('/instagram/engagement', '/instagram/feed-engagement');
  urlRouterProvider.when(
    '/instagram/engagement/posts',
    '/instagram/feed-engagement',
  );
  urlRouterProvider.when(
    '/instagram/engagement/stories',
    '/instagram/story-engagement',
  );
  urlRouterProvider.when(
    '/instagram/hashtags/{tag}',
    '/instagram/hashtags/{tag}/recent-content',
  );
  urlRouterProvider.when(
    '/instagram/competitors',
    '/instagram/competitors/insights',
  );

  // Account for legacy URLs
  urlRouterProvider.when('/instagram/drafts', '/schedule/drafts');
  urlRouterProvider.when('/instagram-account', '/instagram/summary');
  urlRouterProvider.when(
    '/instagram-account/schedule',
    '/instagram/schedule/calendar',
  );
  urlRouterProvider.when(
    '/instagram-account/audience/summary',
    '/instagram/audience',
  );
  urlRouterProvider.when(
    '/instagram-account/engagement',
    '/instagram/feed-engagement',
  );
  urlRouterProvider.when(
    '/instagram-account/engagement/posts',
    '/instagram/feed-engagement',
  );
  urlRouterProvider.when(
    '/instagram-account/engagement/stories',
    '/instagram/story-engagement',
  );
  urlRouterProvider.when(
    '/instagram-account/hashtags/{tag}',
    '/instagram/hashtags/{tag}/recent-content',
  );
  urlRouterProvider.when(
    '/instagram-account/competitors',
    '/instagram/competitors/insights',
  );

  // Legacy Like2Buy routes
  urlRouterProvider.when('/like2buy', '/instagram/like2buy');
  urlRouterProvider.when('/like2buy/moderation', '/instagram/like2buy');

  // Handle all other routes
  urlRouterProvider.when('/instagram-account/*path', '/instagram/{path}');

  if (!hasFeature(FeatureNames.InstagramShopsUgc)) {
    urlRouterProvider.when('/instagram/instagram-shops', '/instagram');
  }
}

export const states = {
  instagramAccount: StateFactory({
    url: '/instagram',
    template: componentNameToTemplate('instagramAccountApp'),
    redirectTo: 'instagramAccount.summary',
    data: {
      title: 'Instagram Account',
      navGroup: NavGroup.PUBLISHING,
    },
    resolve: {
      ...lazyLoadModule(
        'curalate.instagram-account.app',
        () => import('@instagram-account/InstagramAccountApp'),
      ),
    },
  }),

  'instagramAccount.summary': InstagramStateFactory({
    url: '/summary',
    views: {
      'instagram-account-main': {
        template: componentNameToTemplate('instagramAccountSummaryContainer'),
      },
    },
    resolve: {
      ...lazyLoadInstagramBundle(() =>
        // TODO: PD-301371: Remove once feature is released
        hasFeature(FeatureNames.InstagramAnalyticssReactPort)
          ? import(
              '@instagram-account/components/summary/InstagramAccountSummaryApp'
            )
          : import(
              '@instagram-account/instagram-account-summary-container/instagram-account-summary-container'
            ),
      ),
    },
  }),
  'instagramAccount.setup': InstagramStateFactory({
    url: '/setup',
    views: {
      'instagram-account-main': {
        controller: [
          '$state',
          ($state: IStateService) => {
            // This controller shouldn't load unless the user has directly navigated to this url,
            // and they have their accounts hooked up properly.
            // In which case, they don't need to be here! So let's move them to the default page.
            $state.go('instagramAccount.summary', { location: 'replace' });
          },
        ],
        template: '<div></div>',
      },
    },
  }),
  'instagramAccount.audience': InstagramStateFactory({
    url: '/audience',
    views: {
      'instagram-account-main': {
        template: componentNameToTemplate('instagramAccountAudiencePage'),
      },
    },
    resolve: {
      ...lazyLoadInstagramBundle(
        () =>
          import('@instagram-account/instagram-audience/InstagramAudiencePage'),
      ),
    },
  }),
  'instagramAccount.like2buy': StateFactory({
    url: '/like2buy',
    data: {
      title: 'Instagram Like2Buy',
      navGroup: NavGroup.COMMERCE,
    },
    views: {
      'instagram-account-main': {
        template: componentNameToTemplate('like2BuyPage'),
      },
    },
    resolve: {
      ...lazyLoadInstagramBundle(
        () => import('@instagram-account/like2buy/Like2BuyPage'),
      ),
    },
  }),
  'instagramAccount.like2buy-preview': StateFactory({
    url: '/like2buy-preview/:storeId',
    params: { storeId: null },
    data: {
      title: 'Instagram Like2Buy Preview',
      navGroup: NavGroup.COMMERCE,
    },
    views: {
      'instagram-account-main': {
        template: componentNameToTemplate('like2BuyV2PreviewPage'),
      },
    },
    resolve: {
      ...lazyLoadInstagramBundle(
        () =>
          import('@instagram-account/like2buy-v2/page/Like2BuyV2PreviewPage'),
      ),
    },
  }),
  'instagramAccount.l2b-v2': StateFactory({
    url: '/like2buy-v2', // TODO PD-239521: Overwrite /like2buy url
    data: {
      title: 'Instagram Like2Buy',
      hasAccess: () => hasFeature(FeatureNames.InstagramLike2buyV2),
      navGroup: NavGroup.COMMERCE,
    },
    views: {
      'instagram-account-main': {
        template: componentNameToTemplate('like2BuyV2Page'),
      },
    },
    resolve: {
      ...lazyLoadInstagramBundle(
        () => import('@instagram-account/like2buy-v2/page/Like2BuyV2Page'),
      ),
    },
  }),
  'instagramAccount.instagram-shops': StateFactory({
    url: '/instagram-shops',
    data: {
      title: 'Meta Shops UGC',
      hasAccess: () => hasFeature(FeatureNames.InstagramShopsUgc),
      navGroup: NavGroup.COMMERCE,
    },
    views: {
      'instagram-account-main': {
        template: componentNameToTemplate('instagramShopsPage'),
      },
    },
    resolve: {
      ...lazyLoadInstagramBundle(
        () => import('@instagram-account/instagram-shops/InstagramShopsPage'),
      ),
    },
  }),
  'instagramAccount.feedEngagement': InstagramStateFactory({
    url: '/feed-engagement?:startDate&:endDate&:engagementPostFilter&:postViewType',
    views: {
      'instagram-account-main': {
        template: componentNameToTemplate('instagramAccountFeedEngagement'),
      },
    },
    resolve: {
      ...lazyLoadInstagramBundle(() =>
        // TODO: PD-306156: Remove once feature is released
        hasFeature(FeatureNames.InstagramAnalyticssReactPort)
          ? import(
              '@instagram-account/components/engagement/InstagramAccountFeedEngagementApp'
            )
          : import(
              '@instagram-account/instagram-account-engagement/instagram-account-feed-engagement'
            ),
      ),
    },
  }),
  'instagramAccount.storyEngagement': InstagramStateFactory({
    url: '/story-engagement',
    redirectTo: 'instagramAccount.storyEngagement.index',
    views: {
      'instagram-account-main': {
        template: `<div class='flex-tall cu-w-100' ui-view="instagram-account-story-engagement"></div>`,
      },
    },
  }),
  'instagramAccount.storyEngagement.index': InstagramStateFactory({
    url: '?:startDate&:endDate',
    views: {
      'instagram-account-story-engagement': {
        template: componentNameToTemplate('instagramAccountStoryAnalyticsPage'),
      },
    },
  }),
  'instagramAccount.storyEngagement.group': InstagramStateFactory({
    url: '/:date',
    views: {
      'instagram-account-story-engagement': {
        template: componentNameToTemplate(
          'instagramAccountStoryAnalyticsGroupPage',
        ),
      },
    },
  }),
  'instagramAccount.mentionedAndTagged': InstagramStateFactory({
    url: '/mentioned-and-tagged',
    views: {
      'instagram-account-main': {
        template: componentNameToTemplate('instagramMentionedAndTagged'),
      },
    },
    resolve: {
      ...lazyLoadInstagramBundle(
        () =>
          import(
            '@instagram-account/mentioned-and-tagged/InstagramMentionedAndTagged'
          ),
      ),
    },
  }),
  'instagramAccount.competitors': InstagramStateFactory({
    url: '/competitors',
    data: { hasAccess: () => hasFeature(FeatureNames.InstagramCompetitors) },
    views: {
      'instagram-account-main': {
        template: `<div class='flex-tall cu-w-100' ui-view="instagram-competitors"></div>`,
      },
    },
  }),
  'instagramAccount.competitors.insights': InstagramStateFactory({
    url: '/insights',
    data: { hasAccess: () => hasFeature(FeatureNames.InstagramCompetitors) },
    views: {
      'instagram-competitors': {
        template: componentNameToTemplate('instagramCompetitorsInsights'),
      },
    },
    resolve: {
      ...lazyLoadInstagramBundle(
        () =>
          import('@instagram-account/competitors/InstagramCompetitorsInsights'),
      ),
    },
  }),
  'instagramAccount.competitors.media': InstagramStateFactory({
    url: '/media',
    data: { hasAccess: () => hasFeature(FeatureNames.InstagramCompetitors) },
    views: {
      'instagram-competitors': {
        template: componentNameToTemplate('instagramCompetitorsMedia'),
      },
    },
    resolve: {
      ...lazyLoadInstagramBundle(
        () =>
          import('@instagram-account/competitors/InstagramCompetitorsMedia'),
      ),
    },
  }),
  'instagramAccount.hashtagInsights': InstagramStateFactory({
    url: '/hashtags/:hashtag',
    views: {
      'instagram-account-main': {
        template: `<div class='flex-tall cu-w-100' ui-view="source-page"></div>`,
        controller: [
          '$state',
          '$stateParams',
          ($state: IStateService, $stateParams: IStateParamsService) => {
            const hashtag = $stateParams.hashtag;
            const trackedInstagramHashtagSources: IInstagramHashtagTrackedHashtagsResponse =
              $state.$current.locals.globals.trackedInstagramHashtagSources;

            if (
              !trackedInstagramHashtagSources.data.some((s) => s === hashtag)
            ) {
              $state.go('instagramAccount.summary');
            }
          },
        ],
      },
    },
    resolve: {
      trackedInstagramHashtagSources: [
        () => {
          return instagramHashtagApi.getTrackedHashtags();
        },
      ],
    },
  }),
  'instagramAccount.hashtagInsights.recent-content': InstagramStateFactory({
    url: '/recent-content',
    data: {
      hasAccess: () => hasFeature(FeatureNames.HashtagTrackingAndInsights),
    },
    views: {
      'source-page': {
        template: () =>
          componentNameToTemplate('instagramHashtagRecentContent'),
      },
    },
    resolve: {
      ...lazyLoadInstagramBundle(
        () =>
          import(
            '@instagram-account/hashtag-page/InstagramHashtagRecentContent'
          ),
      ),
    },
  }),
  'instagramAccount.hashtagInsights.insights': InstagramStateFactory({
    url: '/insights',
    data: {
      hasAccess: () => hasFeature(FeatureNames.HashtagTrackingAndInsights),
    },
    views: {
      'source-page': {
        template: componentNameToTemplate('instagramHashtagInsights'),
      },
    },
    resolve: {
      ...lazyLoadInstagramBundle(
        () =>
          import('@instagram-account/hashtag-page/InstagramHashtagInsights'),
      ),
    },
  }),
  [PROMOTED_POSTS_ROUTES.PROMOTE_POST]: StateFactory({
    url: '/promote/:sourceId',
    data: {
      title: 'Promoted Posts',
      navGroup: NavGroup.COMMERCE,
    },
    views: {
      'instagram-account-main': {
        template: `<instagram-account-promoted-posts
            source-id="sourceId"
            class="flex-tall"
          ></instagram-account-promoted-posts>`,
        controller: [
          '$scope',
          '$stateParams',
          (
            $scope: Record<string, unknown>,
            $stateParams: IStateParamsService,
          ) => {
            $scope.sourceId = $stateParams.sourceId;
          },
        ],
      },
    },
    resolve: {
      ...lazyLoadInstagramBundle(
        () =>
          import(
            '@instagram-account/instagram-account-promoted-posts/instagram-account-promoted-posts'
          ),
      ),
    },
  }),
  [PROMOTED_POSTS_METRICS_ROUTES.METRICS]: StateFactory({
    url: '/promoted-posts-metrics',
    data: {
      title: 'Promoted Posts',
      navGroup: NavGroup.COMMERCE,
    },
    views: {
      'instagram-account-main': {
        template: componentNameToTemplate(
          'instagramAccountPromotedPostsMetrics',
        ),
      },
    },
    resolve: {
      ...lazyLoadInstagramBundle(
        () =>
          import(
            '@instagram-account/instagram-account-promoted-posts-metrics/instagram-account-promoted-posts-metrics'
          ),
      ),
    },
  }),
  'instagramAccount.shopTagsMetrics': InstagramStateFactory({
    url: '/shop-tags',
    views: {
      'instagram-account-main': {
        template: componentNameToTemplate('instagramShopTagsMetricsContainer'),
      },
    },
    resolve: {
      ...lazyLoadSubModule(
        ['curalate.shop-tags-metrics', 'curalate.analytics-at-a-glance'],
        () =>
          import(
            '@instagram-account/instagram-account-shop-tags-metrics/instagram-shop-tags-metrics-container'
          ),
      ),
    },
  }),
  'instagramAccount.image-insights': InstagramStateFactory({
    url: '/image-insights',
    views: {
      'instagram-account-main': {
        template: componentNameToTemplate('imageInsightsPage'),
      },
    },
    resolve: {
      ...lazyLoadSubModule(
        ['curalate.imageInsights'],
        () => import('app/image-insights/image-insights.app'),
      ),
    },
  }),
};
